/** Pure room-board (객실현황판) logic — status per room from rooms + reservations.
 *
 * Dates are ISO `YYYY-MM-DD` strings, compared lexicographically.
 */
import type { Reservation, Room } from "@/types";

export type RoomBoardStatus =
  | "vacant" // 공실
  | "reserved" // 체크인 전 예약 (배정됨)
  | "occupied" // 재실
  | "checkout_due" // 체크아웃 임박 (오늘 퇴실)
  | "checkout_over" // 체크아웃 초과
  | "unavailable"; // 사용불가 (비활성 · 블랙아웃)

export interface RoomBoardCell {
  room: Room;
  status: RoomBoardStatus;
  reservation: Reservation | null;
}

const ACTIVE = new Set(["booked", "checked_in"]);

export function isRoomUnavailable(room: Room, today: string): boolean {
  if (!room.is_active) return true;
  // Blackout ranges are half-open [start, end).
  return (room.blackouts ?? []).some((b) => b.start <= today && today < b.end);
}

/** The reservation bound to a room: the checked-in one first, else the next
 *  pre-assigned booking that hasn't ended yet. */
export function reservationForRoom(
  room: Room,
  reservations: Reservation[],
  today: string
): Reservation | null {
  const mine = reservations.filter((r) => r.room_id === room.id && ACTIVE.has(r.status));
  const checkedIn = mine.find((r) => r.status === "checked_in");
  if (checkedIn) return checkedIn;
  return (
    mine
      .filter((r) => r.check_out > today)
      .sort((a, b) => a.check_in.localeCompare(b.check_in))[0] ?? null
  );
}

export function roomBoardCell(
  room: Room,
  reservations: Reservation[],
  today: string
): RoomBoardCell {
  const reservation = reservationForRoom(room, reservations, today);
  if (isRoomUnavailable(room, today)) return { room, status: "unavailable", reservation };
  if (!reservation) return { room, status: "vacant", reservation: null };
  if (reservation.status === "checked_in") {
    // ponytail: date-granularity only; time-of-day (30분 전 경고) needs
    // property check_out_time — add when the board grows a clock.
    if (reservation.check_out < today) return { room, status: "checkout_over", reservation };
    if (reservation.check_out === today) return { room, status: "checkout_due", reservation };
    return { room, status: "occupied", reservation };
  }
  return { room, status: "reserved", reservation };
}

/** Booked reservations with no room yet — the unassigned tray. */
export function unassignedReservations(reservations: Reservation[], query = ""): Reservation[] {
  const q = query.trim().toLowerCase();
  return reservations
    .filter((r) => r.status === "booked" && !r.room_id)
    .filter((r) => !q || r.guest_name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q))
    .sort((a, b) => a.check_in.localeCompare(b.check_in));
}

/** Group rooms by floor, keeping a stable floor order; null floors last. */
export function groupRoomsByFloor(rooms: Room[]): [string, Room[]][] {
  const groups = new Map<string, Room[]>();
  for (const room of rooms) {
    const key = room.floor?.trim() || "기타";
    groups.set(key, [...(groups.get(key) ?? []), room]);
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === "기타" ? 1 : b === "기타" ? -1 : a.localeCompare(b, "ko", { numeric: true })
  );
}

/** Local calendar date as ISO YYYY-MM-DD (front desk works in local time). */
export function todayLocalISO(): string {
  return new Date().toLocaleDateString("en-CA");
}
